export interface CropPixelArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = src;
  });

/**
 * Recorta uma imagem a partir da área selecionada (em pixels da imagem
 * original) e devolve um Blob JPEG pronto para upload.
 */
export const getCroppedImageBlob = async (
  imageSrc: string,
  crop: CropPixelArea,
  outputSize = 512
): Promise<Blob> => {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível preparar o recorte da imagem.');

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Não foi possível gerar a imagem recortada.'));
      },
      'image/jpeg',
      0.9
    );
  });
};

/**
 * Baixa uma imagem de uma URL externa e devolve uma Object URL local.
 * Usa um proxy público de imagens para evitar bloqueios de CORS, já que a
 * maioria dos servidores de imagem não libera acesso via fetch() direto do
 * navegador para outros domínios.
 */
export const fetchImageFromUrl = async (url: string): Promise<{ blob: Blob; objectUrl: string }> => {
  const trimmed = url.trim();
  if (!trimmed) throw new Error('Informe uma URL de imagem.');

  const proxiedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(
    trimmed.replace(/^https?:\/\//, '')
  )}`;

  const response = await fetch(proxiedUrl);
  if (!response.ok) {
    throw new Error('Não foi possível baixar a imagem dessa URL.');
  }

  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) {
    throw new Error('O link informado não retornou uma imagem válida.');
  }

  return { blob, objectUrl: URL.createObjectURL(blob) };
};

export const blobToFile = (blob: Blob, fileName: string): File =>
  new File([blob], fileName, { type: blob.type || 'image/jpeg' });
